import { z } from 'zod';
import { isoDateSchema } from './common.ts';

/**
 * Barn logs are an APPEND-ONLY ledger. Entries are never edited or deleted;
 * a wrong entry is voided (with a reason) and a corrected one is added.
 * `clientEntryId` is a UUIDv7 made on the phone, so an entry replayed from the
 * offline outbox (even days later) is stored exactly once.
 */
export const barnLogAddInputSchema = z
  .object({
    clientEntryId: z.uuidv7(),
    barnNumber: z.number().int().min(1).max(99),
    logDate: isoDateSchema,
    deadCount: z.number().int().min(0).max(100_000).optional(),
    feedLb: z.number().min(0).max(1_000_000).optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((v) => v.deadCount !== undefined || v.feedLb !== undefined, {
    message: 'Enter dead birds, feed used, or both',
    path: ['deadCount'],
  });
export type BarnLogAddInput = z.infer<typeof barnLogAddInputSchema>;

export const barnLogVoidInputSchema = z.object({
  entryId: z.uuid(),
  reason: z.string().trim().min(3, 'Say why this entry is wrong').max(500),
});
export type BarnLogVoidInput = z.infer<typeof barnLogVoidInputSchema>;

export const barnLogRecentInputSchema = z.object({
  barnNumber: z.number().int().min(1).max(99).optional(),
  days: z.number().int().min(1).max(366).default(14),
});
export type BarnLogRecentInput = z.input<typeof barnLogRecentInputSchema>;

export const barnLogEntrySchema = z.object({
  id: z.uuid(),
  barnNumber: z.number().int(),
  logDate: isoDateSchema,
  deadCount: z.number().int().nullable(),
  feedLb: z.number().nullable(),
  note: z.string().nullable(),
  enteredBy: z.string(),
  enteredAt: z.iso.datetime(),
  voidedAt: z.iso.datetime().nullable(),
  voidReason: z.string().nullable(),
});
export type BarnLogEntry = z.infer<typeof barnLogEntrySchema>;
